import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Image, Modal, Platform, ScrollView, TouchableOpacity, View } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';

import { getFileUrl } from '../../services/api';
import { UnifiedEvent } from '../../types/auth';
import { Typography } from '../ui';
import { ShareModal } from '../ShareModal';
import { formatEventDuration } from '../../utils/eventDuration';
import { buildShareText, shareOrOpenModal } from '../../utils/roomShare';
import type { ShareTarget } from '../../services/shareAnalytics';
import { useAuth } from '../../context/AuthContext';
import { useAuthCTA } from '../../context/AuthCTAContext';
import { useCalendar } from '../../context/CalendarContext';
import { useRepost } from '../../context/RepostContext';
import { repostTargetForEvent } from '../../services/repost';
import { fontWeights } from '../../styles/typographyTokens';
import { EventReportOffer } from '../eventReport/EventReportOffer';

import { ACCENT_TEXT, ON_ACCENT_TEXT } from '../../styles/accentText';
type OfflineEventDetailsModalProps = {
  event: UnifiedEvent | null;
  isDark: boolean;
  language: string;
  onClose: () => void;
  themeColors: {
    textPrimary: string;
    textSecondary: string;
    surface?: string;
  };
};

export const OfflineEventDetailsModal: React.FC<OfflineEventDetailsModalProps> = ({
  event,
  isDark,
  language,
  onClose,
  themeColors,
}) => {
  const [shareOpen, setShareOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const { showAuthCTA } = useAuthCTA();
  const { openRepost } = useRepost();
  const {
    offlineRegistrationIds,
    offlineRegistrationPendingIds,
    offlineRegistrationErrorIds,
    toggleOfflineEventRegistration,
    seedOfflineEventRegistration,
  } = useCalendar();

  useEffect(() => {
    if (event?.isRegistered) seedOfflineEventRegistration(event.id, true);
  }, [event?.id, event?.isRegistered, seedOfflineEventRegistration]);

  if (!event) return null;

  const coverUrl = getFileUrl(event.coverImageUrl);
  const locale = language === 'ru' ? 'ru-RU' : 'en-US';
  const start = new Date(event.startDateUtc);
  const end = event.endDateUtc ? new Date(event.endDateUtc) : null;
  const dateLabel = start.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  // CORE-225 — duration is the start→end delta (multi-day events run for
  // days). Only append the end time to the range when it's the same calendar
  // day; for a multi-day event "13:30 - 13:30" would be meaningless, so the
  // «Длительность» chip below carries the span instead.
  const durationMinutes = end ? Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000)) : 0;
  const sameDay = end ? start.toDateString() === end.toDateString() : true;
  const startTimeLabel = start.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const timeLabel = end && sameDay
    ? `${startTimeLabel} - ${end.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`
    : startTimeLabel;
  const share = buildShareText({ type: 'event', id: event.id, title: event.title });
  const shareTarget: ShareTarget = { entityType: 'event', entityId: event.id, surface: 'event_card' };

  const rows = [
    event.location ? { icon: 'location-outline' as const, label: language === 'ru' ? 'Локация' : 'Location', value: event.location } : null,
    event.address && event.address !== event.location ? { icon: 'navigate-outline' as const, label: language === 'ru' ? 'Адрес' : 'Address', value: event.address } : null,
    event.contactPhone ? { icon: 'call-outline' as const, label: language === 'ru' ? 'Телефон' : 'Phone', value: event.contactPhone } : null,
    event.contactName ? { icon: 'person-outline' as const, label: language === 'ru' ? 'Контакт' : 'Contact', value: event.contactName } : null,
    event.price ? { icon: 'card-outline' as const, label: language === 'ru' ? 'Стоимость' : 'Price', value: String(event.price) } : null,
  ].filter(Boolean) as Array<{ icon: any; label: string; value: string }>;
  const isRegistered = offlineRegistrationIds.has(event.id);
  const isPending = offlineRegistrationPendingIds.has(event.id);
  const hasError = offlineRegistrationErrorIds.has(event.id);
  const handleRsvp = () => {
    if (!isAuthenticated) {
      showAuthCTA('gated_action', language === 'ru' ? 'Войдите, чтобы записаться на событие' : 'Sign in to RSVP');
      return;
    }
    void toggleOfflineEventRegistration(event).catch(() => {});
  };
  // CORE-1693 (QA 17.10) — событие без анонса репостится только отсюда: в
  // ленте у него нет карточки, а календарь его деталей не показывает. Тот же
  // `event/master`, что и у карточки календаря; гостя уводит во вход провайдер.
  const handleRepost = () => {
    openRepost(repostTargetForEvent(event.id, 'master'), {
      kind: 'event',
      title: event.title,
      text: event.description ?? null,
      authorName: event.organizerName ?? null,
      coverUrl: event.coverImageUrl ?? null,
      startsAtUtc: event.startDateUtc,
    });
  };

  return (
    <>
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <TouchableOpacity activeOpacity={1} onPress={onClose} style={{ position: 'absolute', inset: 0 as any, zIndex: 0 }} />
        <View
          testID="offline-event-detail"
          style={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            maxWidth: 620,
            maxHeight: '88%',
            borderRadius: 24,
            overflow: 'hidden',
            backgroundColor: themeColors.surface || (isDark ? '#1C1C1E' : '#FFF'),
            ...Platform.select({
              web: { boxShadow: '0 24px 80px rgba(0,0,0,0.35)' } as any,
            }),
          }}
        >
          <ScrollView showsVerticalScrollIndicator={false}>
            <View style={{ height: 240, backgroundColor: isDark ? '#111' : '#EEE' }}>
              {coverUrl ? (
                <Image source={{ uri: coverUrl }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
              ) : null}
              <View style={{ position: 'absolute', inset: 0 as any, backgroundColor: 'rgba(0,0,0,0.25)' }} />
              <View style={{ position: 'absolute', top: 14, right: 14, zIndex: 2, flexDirection: 'row', gap: 10 }}>
                {/* CORE-978 — «Поделиться» was missing from the viewer opened from
                    both the feed and the calendar. Routes through the shared
                    helper so the link is the /lives?eventId= deep link. */}
                <TouchableOpacity
                  onPress={() => {
                    // CORE-786 — a phone gets the OS sheet, a desktop the popup.
                    void shareOrOpenModal(
                      { url: share.url, title: event.title, subject: share.subject, message: share.message },
                      shareTarget,
                      () => setShareOpen(true),
                    );
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={language === 'ru' ? 'Поделиться' : 'Share'}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 17,
                    backgroundColor: 'rgba(0,0,0,0.55)',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : null),
                  }}
                >
                  <Ionicons name="share-social-outline" size={18} color="#FFF" />
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={onClose}
                  accessibilityRole="button"
                  accessibilityLabel={language === 'ru' ? 'Закрыть' : 'Close'}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 17,
                    backgroundColor: 'rgba(0,0,0,0.55)',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Ionicons name="close" size={18} color="#FFF" />
                </TouchableOpacity>
              </View>
              <View style={{ position: 'absolute', left: 20, right: 20, bottom: 18 }}>
                <View style={{ alignSelf: 'flex-start', backgroundColor: '#E98245', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 5, marginBottom: 10 }}>
                  <Typography variant="overline" style={{ color: ON_ACCENT_TEXT, fontWeight: fontWeights.bold, fontSize: 11 }}>
                    {language === 'ru' ? 'ОФЛАЙН' : 'OFFLINE'}
                  </Typography>
                </View>
                <Typography variant="h2" style={{ color: '#FFF', fontWeight: fontWeights.bold }}>
                  {event.title}
                </Typography>
                {event.subtitle ? (
                  <Typography variant="body" style={{ color: 'rgba(255,255,255,0.82)', marginTop: 6 }}>
                    {event.subtitle}
                  </Typography>
                ) : null}
              </View>
            </View>

            <View style={{ padding: 22 }}>
              <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Ionicons name="calendar-outline" size={18} color={ACCENT_TEXT} />
                  <Typography variant="body" style={{ color: themeColors.textPrimary, fontWeight: fontWeights.bold }}>{dateLabel}</Typography>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Ionicons name="time-outline" size={18} color={ACCENT_TEXT} />
                  <Typography variant="body" style={{ color: themeColors.textPrimary, fontWeight: fontWeights.bold }}>{timeLabel}</Typography>
                </View>
                {durationMinutes > 0 ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Ionicons name="hourglass-outline" size={18} color={ACCENT_TEXT} />
                    <Typography variant="body" style={{ color: themeColors.textPrimary, fontWeight: fontWeights.bold }}>
                      {(language === 'ru' ? 'Длительность: ' : 'Duration: ') + formatEventDuration(durationMinutes)}
                    </Typography>
                  </View>
                ) : null}
              </View>

              {event.description ? (
                <Typography variant="body" style={{ color: themeColors.textSecondary, lineHeight: 24, marginBottom: 18 }}>
                  {event.description}
                </Typography>
              ) : null}

              {event.tags?.length ? (
                <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
                  {event.tags.map(tag => (
                    <View key={tag} style={{ borderRadius: 999, paddingHorizontal: 12, paddingVertical: 7, backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#F4F4F4' }}>
                      <Typography variant="bodySmall" style={{ color: themeColors.textPrimary, fontWeight: '600' }}>{tag}</Typography>
                    </View>
                  ))}
                </View>
              ) : null}

              <View style={{ gap: 12 }}>
                {rows.map(row => (
                  <View key={`${row.label}-${row.value}`} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
                    <Ionicons name={row.icon} size={18} color={ACCENT_TEXT} style={{ marginTop: 2 }} />
                    <View style={{ flex: 1 }}>
                      <Typography variant="bodySmall" style={{ color: themeColors.textSecondary, marginBottom: 2 }}>{row.label}</Typography>
                      <Typography variant="body" style={{ color: themeColors.textPrimary, fontWeight: '600' }}>{row.value}</Typography>
                    </View>
                  </View>
                ))}
              </View>
              {/* CORE-1230 — автору прошедшего офлайн-события: рассказать, как
                  прошло. На обеих платформах — отчёт пишется и с телефона. */}
              <EventReportOffer
                event={event}
                onLeave={onClose}
                textPrimary={themeColors.textPrimary}
                textSecondary={themeColors.textSecondary}
                isDark={isDark}
              />
              {Platform.OS === 'web' ? (
                <View style={{ marginTop: 22, gap: 8 }}>
                  <TouchableOpacity
                    testID="offline-event-rsvp"
                    onPress={handleRsvp}
                    disabled={isPending}
                    accessibilityRole="button"
                    accessibilityLabel={isRegistered
                      ? (language === 'ru' ? 'Отменить участие' : 'Cancel RSVP')
                      : (language === 'ru' ? 'Присоединиться' : 'Join')}
                    accessibilityState={{ disabled: isPending, busy: isPending }}
                    style={{
                      minHeight: 48,
                      borderRadius: 14,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isRegistered ? (isDark ? '#34302D' : '#F2ECE7') : '#E98245',
                    }}
                  >
                    {isPending ? (
                      <ActivityIndicator color={isRegistered ? (isDark ? '#FFF' : '#332720') : ON_ACCENT_TEXT} />
                    ) : (
                      <Typography variant="body" style={{ color: isRegistered ? (isDark ? '#FFF' : '#332720') : ON_ACCENT_TEXT, fontWeight: fontWeights.bold }}>
                        {isRegistered
                          ? (language === 'ru' ? 'Отменить участие' : 'Cancel RSVP')
                          : (language === 'ru' ? 'Присоединиться' : 'Join')}
                      </Typography>
                    )}
                  </TouchableOpacity>
                  {hasError ? (
                    <Typography variant="bodySmall" style={{ color: isDark ? '#FFB6A3' : '#9D3E27' }}>
                      {language === 'ru' ? 'Не удалось изменить участие. Попробуйте ещё раз.' : 'Could not update RSVP. Please try again.'}
                    </Typography>
                  ) : null}
                  <TouchableOpacity
                    testID="offline-event-repost"
                    onPress={handleRepost}
                    accessibilityRole="button"
                    accessibilityLabel={language === 'ru' ? 'Репостнуть в свою ленту' : 'Repost to your feed'}
                    style={{
                      minHeight: 48,
                      borderRadius: 14,
                      borderWidth: 1,
                      borderColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(15,20,25,0.14)',
                      flexDirection: 'row',
                      gap: 8,
                      alignItems: 'center',
                      justifyContent: 'center',
                      ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : null),
                    }}
                  >
                    <Ionicons name="repeat-outline" size={18} color={themeColors.textPrimary} />
                    <Typography variant="body" style={{ color: themeColors.textPrimary, fontWeight: fontWeights.bold }}>
                      {language === 'ru' ? 'Репост в свою ленту' : 'Repost to your feed'}
                    </Typography>
                  </TouchableOpacity>
                </View>
              ) : null}
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
    <ShareModal
      visible={shareOpen}
      onClose={() => setShareOpen(false)}
      url={share.url}
      title={event.title}
      subject={share.subject}
      message={share.message}
      language={language}
      analytics={shareTarget}
    />
    </>
  );
};
